import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Ban, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { blockUser } from "@/lib/blocks";

// Confirmation before blocking a member (shared by posts, comments, profile, chat).
export const BlockUserDialog = ({ open, onOpenChange, user, onBlocked, testidPrefix = "block" }) => {
  const { t } = useApp();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const name = user?.name || "";
  // Lot 636 : compte protégé (super admin / « Yalola App ») → jamais de dialogue de blocage, quel que soit l'appelant.
  if (user?.is_protected) return null;

  const confirm = async () => {
    if (!user?.id || busy) return;
    setBusy(true);
    try {
      await blockUser(user.id, reason.trim());
      toast.success(t("block.done", "{name} a été bloqué·e").replace("{name}", name));
      onOpenChange(false);
      setReason("");
      onBlocked?.(user.id);
    } catch (e) {
      toast.error(e?.friendlyMessage || "Erreur");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md z-[95]" data-testid={`${testidPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Ban size={18} className="text-red-600" /> {t("block.title", "Bloquer {name} ?").replace("{name}", name)}</DialogTitle>
          <DialogDescription>{t("block.desc", "Ses publications, stories, commentaires et messages disparaîtront immédiatement de ton expérience. Vous ne pourrez plus vous contacter ni vous suivre. Notre équipe de modération est informée de ce blocage.")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <label className="text-xs font-semibold" style={{ color: "var(--yalola-text-muted)" }}>{t("block.reason_label", "Raison (facultatif)")}</label>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={300}
            placeholder={t("block.reason_placeholder", "Pourquoi bloques-tu cette personne ?")} data-testid={`${testidPrefix}-reason`} />
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={() => onOpenChange(false)} disabled={busy} data-testid={`${testidPrefix}-cancel`}>{t("common.cancel", "Annuler")}</Button>
          <Button className="rounded-full bg-red-600 hover:bg-red-700 text-white" onClick={confirm} disabled={busy} data-testid={`${testidPrefix}-confirm`}>
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Ban size={15} className="mr-1.5" />} {t("block.confirm", "Bloquer")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BlockUserDialog;
